export const TAX_RULES: Record<string, { rate: number; text?: string }> = {
  au: { rate: 0.10, text: "GST" },
  sg: { rate: 0.09, text: "GST" },
  my: { rate: 0.08, text: "SST" },
  nz: { rate: 0.15, text: "GST" },

  // no tax text / 0 tax
  hk: { rate: 0 },
  gb: { rate: 0 },
  gl: { rate: 0 },
};


type TaxSettingItem = {
  countryCode?: { value?: string };
  taxLabel?: { value?: string };
  taxPercentage?: { value?: string };
};

type TaxRules = Record<string, { rate: number; text?: string }>;

export const buildTaxRulesFromSettings = (settings: unknown, fallback: TaxRules): TaxRules => {
  if (!Array.isArray(settings)) return fallback;

  const rules: TaxRules = {};

  for (const s of settings as TaxSettingItem[]) {
    const code = (s?.countryCode?.value || "").trim().toLowerCase();
    if (!code) continue;

    const text = (s?.taxLabel?.value || "").trim();
    const pctRaw = (s?.taxPercentage?.value || "").trim();

    // taxPercentage: "10" => 0.10
    const pctNum = Number(pctRaw);
    const rate =
      Number.isFinite(pctNum) && pctNum > 0
        ? pctNum / 100
        : 0;

    rules[code] = { rate, ...(text ? { text } : {}) };
  }

  // fallback
  return Object.keys(rules).length ? rules : fallback;
};

// price -> cents（integer），keep 2 decimals
// - "67.2" -> 6720
// - "99.25" -> 9925
// - "837.237" -> 83724   
// - invalid -> null
export const toCentsSafe = (amount: unknown): number | null => {
  if (amount === null || amount === undefined) return null;

  const raw = String(amount)
    .trim()
    .replace(/,/g, '')
    .replace(/^\$/, '');

  if (raw === '') return null;

  // for integer bigger than 0
  const m = raw.match(/^(\d+)(?:\.(\d+))?$/);
  if (!m) return null;

  const intPart = m[1];
  const decPart = m[2] ?? '';

  const dollars = parseInt(intPart, 10);
  if (!Number.isFinite(dollars)) return null;

  // the first 3 decimals for rounding
  const d0 = decPart.charCodeAt(0) ? decPart[0] : '0';
  const d1 = decPart.charCodeAt(1) ? decPart[1] : '0';
  const d2 = decPart.charCodeAt(2) ? decPart[2] : '0';

  let cents = dollars * 100 + (parseInt(d0, 10) * 10 + parseInt(d1, 10));

  // round half up
  if (parseInt(d2, 10) >= 5) cents += 1;

  return cents;
};

export const centsToFixed2 = (cents: number): string =>
  Number.isFinite(cents) ? (cents / 100).toFixed(2) : '0.00';

export const calcTaxInclusivePriceSafe = (basePrice: unknown, rate: number): string | null => {
  const baseCents = toCentsSafe(basePrice);
  if (baseCents === null) return null;

  const r = Number.isFinite(rate) && rate > 0 ? rate : 0;
  const taxCents = Math.round(baseCents * r); 
  return centsToFixed2(baseCents + taxCents);
};

type RelatedModuleItem = {
  cpdHours?: { value?: string };
};

// Bundle Course Pages have no CPD hours of their own; the page total is the
// sum of the CPD hours of every related module. Returns null when there is
// nothing to total (non-bundle pages get relatedModules: null).
export const sumRelatedModulesCpdHours = (targetItems: unknown): number | null => {
  if (!Array.isArray(targetItems) || targetItems.length === 0) return null;

  let total = 0;
  let hasValue = false;

  for (const item of targetItems as RelatedModuleItem[]) {
    const raw = String(item?.cpdHours?.value ?? '').trim();
    if (raw === '') continue;

    const hours = Number(raw);
    if (!Number.isFinite(hours) || hours < 0) continue;

    total += hours;
    hasValue = true;
  }

  // CPD hours are authored as whole numbers or halves (1, 1.5, 20.5), which are
  // exact in binary float, so this sum does not drift and the snap below is a
  // no-op for real data. It only guards a malformed extract value such as 1.1,
  // where float addition would otherwise render "3.3000000000000003".
  return hasValue ? Math.round(total * 100) / 100 : null;
};
